import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, fmtBRL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { Download, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { localDateStr } from "@/pages/entregas/entregasHelpers";

const PRESETS = [
  { key: "hoje", label: "Hoje" }, { key: "7d", label: "7 dias" }, { key: "mes", label: "Este mês" }, { key: "30d", label: "30 dias" },
];
const rangeFor = (kind) => {
  const today = localDateStr();
  const d = new Date();
  if (kind === "hoje") return { start: today, end: today };
  if (kind === "7d") { d.setDate(d.getDate() - 6); return { start: localDateStr(d), end: today }; }
  if (kind === "30d") { d.setDate(d.getDate() - 29); return { start: localDateStr(d), end: today }; }
  return { start: `${today.slice(0, 7)}-01`, end: today };
};
const fmtMin = (m) => (m == null ? "—" : m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}min` : `${Math.round(m)} min`);
const pct = (v) => `${(v ?? 0).toFixed(1).replace(".", ",")}%`;

// Bloco 8 (Iter 99): desempenho por rota no período — tabela + gráfico + CSV.
export function RotasDesempenho() {
  const { user, currentEmpresaId } = useAuth();
  const canFin = hasPerm(user, "financeiro", "v");
  const [period, setPeriod] = useState(rangeFor("7d"));
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/rotas", { params: period });
      setRep(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar relatório"); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load, currentEmpresaId]);

  const rows = useMemo(() => (rep ? [...rep.rotas, ...(rep.sem_rota ? [rep.sem_rota] : [])] : []), [rep]);
  const chartData = useMemo(() => rows.filter((r) => r.total > 0).map((r) => ({
    nome: r.nome, cor: r.cor, Concluídas: r.concluidas, Canceladas: r.canceladas, Outras: r.total - r.concluidas - r.canceladas,
  })), [rows]);
  const activePreset = PRESETS.find((p) => { const r = rangeFor(p.key); return r.start === period.start && r.end === period.end; })?.key;

  const exportCsv = async () => {
    try {
      const headers = currentEmpresaId ? { "X-Empresa-Id": currentEmpresaId } : {};
      const r = await fetch(`${API}/reports/rotas/csv?start=${period.start}&end=${period.end}`, { credentials: "include", headers });
      if (!r.ok) throw new Error("Falha ao gerar CSV");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `rotas_${period.start}_${period.end}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch (e) { toast.error(e.message || "Erro ao exportar"); }
  };

  const t = rep?.totals;
  return (
    <div className="space-y-4" data-testid="rotas-desempenho">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-600" />Período</CardTitle>
          <div className="flex gap-1 flex-wrap" data-testid="rotas-rep-presets">
            {PRESETS.map((p) => (
              <Button key={p.key} size="sm" variant="outline" aria-pressed={activePreset === p.key} onClick={() => setPeriod(rangeFor(p.key))}
                className={`h-7 text-xs ${activePreset === p.key ? "bg-orange-600 text-white border-orange-600 hover:bg-orange-700 hover:text-white" : ""}`}
                data-testid={`rotas-rep-preset-${p.key}`}>{p.label}</Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-xs">De</Label><Input type="date" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })} data-testid="rotas-rep-start" /></div>
          <div><Label className="text-xs">Até</Label><Input type="date" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })} data-testid="rotas-rep-end" /></div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button variant="outline" onClick={exportCsv} disabled={!rep} data-testid="rotas-rep-csv"><Download className="w-4 h-4 mr-2" />Exportar CSV</Button>
          </div>
        </CardContent>
      </Card>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="rotas-rep-totais">
          {[["Total", t.total, "text-slate-800"], ["Concluídas", t.concluidas, "text-emerald-700"], ["Canceladas", t.canceladas, "text-rose-700"],
            ["Pendentes", t.pendentes + t.em_transito, "text-amber-700"], ["Taxa de conclusão", pct(t.taxa_conclusao), "text-orange-700"]].map(([l, v, c]) => (
            <div key={l} className="rounded-xl border bg-white p-3">
              <div className="text-[11px] uppercase text-muted-foreground font-semibold">{l}</div>
              <div className={`text-2xl font-extrabold ${c}`} data-testid={`rotas-rep-total-${l.toLowerCase().split(" ")[0]}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Entregas por rota no período</CardTitle></CardHeader>
        <CardContent style={{ height: 280 }} className="min-w-0">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="99%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="nome" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Legend />
                <Bar dataKey="Concluídas" stackId="a" radius={[0, 0, 0, 0]}>
                  {chartData.map((d) => <Cell key={d.nome} fill={d.cor} />)}
                </Bar>
                <Bar dataKey="Canceladas" stackId="a" fill="#F43F5E" />
                <Bar dataKey="Outras" stackId="a" fill="#CBD5E1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-center text-muted-foreground py-16">{loading ? "Carregando…" : "Sem entregas no período"}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Detalhamento por rota</CardTitle>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-orange-500" />}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Rota</TableHead><TableHead className="text-center">Bairros</TableHead><TableHead className="text-center">Total</TableHead>
              <TableHead className="text-center">Concluídas</TableHead><TableHead className="text-center">Canceladas</TableHead>
              <TableHead className="text-center">Remarcadas</TableHead><TableHead className="text-center">Pendentes</TableHead>
              <TableHead className="text-center">Em trânsito</TableHead><TableHead className="text-center">Taxa</TableHead>
              <TableHead className="text-center">Tempo médio</TableHead><TableHead className="text-center">Entregadores</TableHead>
              {canFin && <TableHead className="text-right">Faturado</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.rota_id || "none"} data-testid={`rotas-rep-row-${r.rota_id || "none"}`} className={r.total === 0 ? "opacity-60" : ""}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: r.cor }} />
                      <span className="font-medium">{r.nome}</span>
                      {r.ativa === false && <span className="text-[10px] uppercase text-slate-500 bg-slate-100 rounded px-1">inativa</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-xs">{r.rota_id ? r.bairros_count : "—"}</TableCell>
                  <TableCell className="text-center font-bold" data-testid={`rotas-rep-total-${r.rota_id || "none"}`}>{r.total}</TableCell>
                  <TableCell className="text-center text-emerald-700 font-semibold">{r.concluidas}</TableCell>
                  <TableCell className="text-center text-rose-700">{r.canceladas}</TableCell>
                  <TableCell className="text-center text-amber-700">{r.remarcadas}</TableCell>
                  <TableCell className="text-center">{r.pendentes}</TableCell>
                  <TableCell className="text-center text-blue-700">{r.em_transito}</TableCell>
                  <TableCell className="text-center">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-14 h-1.5 rounded bg-slate-200 overflow-hidden"><div className="h-full" style={{ width: `${r.taxa_conclusao}%`, backgroundColor: r.cor }} /></div>
                      <span className="text-xs font-semibold">{pct(r.taxa_conclusao)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-xs mono">{fmtMin(r.tempo_medio_min)}</TableCell>
                  <TableCell className="text-center">{r.entregadores_count}</TableCell>
                  {canFin && <TableCell className="text-right mono">{fmtBRL(r.valor_concluidas || 0)}</TableCell>}
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={canFin ? 12 : 11} className="text-center text-muted-foreground py-8">{loading ? "Carregando…" : "Nenhuma rota cadastrada"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
